import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import Header from '../components/Header';

const C = {
  trasporto: '#EB1C24',
  alloggio: '#0071BA',
  attivita: '#FF5B00',
  servizi: '#009045',
  dark: '#0a1628',
  white: '#ffffff',
  text: '#1a1a2e',
  textLight: '#6b7280',
  border: '#e5e7eb',
  light: '#f5f7fa',
};

const ADMIN_EMAIL = 'pasquettofederico@hotmail.it';

export default function Dashboard() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ create: 0, pubblicate: 0, copiate: 0 });
  const [adminStats, setAdminStats] = useState(null);
  const [ultimaEsperienza, setUltimaEsperienza] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let done = false;

    const loadData = async (user) => {
      try {
        // Profilo
        const { data: existing } = await supabase
          .from('profiles').select('*').eq('id', user.id).single();

        let prof = existing;
        if (!existing) {
          const username = user.user_metadata?.full_name || user.user_metadata?.name ||
                           user.user_metadata?.username || user.email?.split('@')[0] || 'utente';
          await supabase.from('profiles').upsert({ id: user.id, username, livello: 1, punteggio: 0 });
          prof = { id: user.id, username, livello: 1, punteggio: 0 };
        }
        if (mounted) { setProfile(prof); setUserEmail(user.email || ''); }

        // Esperienze utente
        const { data: esperienze } = await supabase
          .from('esperienze').select('*').eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (mounted && esperienze) {
          setStats({
            create: esperienze.length,
            pubblicate: esperienze.filter(e => e.is_pubblica).length,
            copiate: esperienze.reduce((s, e) => s + (e.volte_copiata || 0), 0),
          });
          setUltimaEsperienza(esperienze[0] || null);
        }

        // Admin stats
        if (user.email === ADMIN_EMAIL) {
          const [{ count: totUtenti }, { count: totEsp }, { count: totAdopters }, { data: ultime }] = await Promise.all([
            supabase.from('profiles').select('*', { count: 'exact', head: true }),
            supabase.from('esperienze').select('*', { count: 'exact', head: true }).eq('is_pubblica', true),
            supabase.from('early_adopters').select('*', { count: 'exact', head: true }),
            supabase.from('esperienze').select('*, profiles(username)').eq('is_pubblica', true)
              .order('created_at', { ascending: false }).limit(5),
          ]);
          if (mounted) setAdminStats({ totUtenti, totEsp, totAdopters, ultime: ultime || [] });
        }
      } catch (_) {
      } finally {
        if (mounted) setLoading(false);
      }
    };

    const handleSession = (session) => {
      if (done || !mounted) return;
      done = true;
      if (session) loadData(session.user);
      else navigate('/login');
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) handleSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') handleSession(session);
      else if (event === 'SIGNED_OUT') navigate('/login');
    });

    const timer = setTimeout(() => { if (mounted && !done) navigate('/login'); }, 4000);

    return () => { mounted = false; clearTimeout(timer); subscription.unsubscribe(); };
  }, [navigate]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: C.light }}>
      <p style={{ color: C.textLight, fontSize: '15px', fontFamily: 'sans-serif' }}>Caricamento...</p>
    </div>
  );

  const isAdmin = userEmail === ADMIN_EMAIL;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.light, fontFamily: 'Roboto, sans-serif', color: C.text }}>
      <style>{`
        @font-face { font-family: 'Roboto'; src: url('/fonts/Roboto-Regular.ttf'); font-weight: 400; }
        @font-face { font-family: 'Roboto'; src: url('/fonts/Roboto-Bold.ttf'); font-weight: 700; }
        @font-face { font-family: 'Roboto'; src: url('/fonts/Roboto-Black.ttf'); font-weight: 900; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        a { text-decoration: none; } button:hover { opacity: 0.85; cursor: pointer; }
        @media (max-width: 1024px) {
          .octa-header { padding: 12px 24px !important; }
          .octa-footer { padding: 24px 24px !important; }
        }
        @media (max-width: 640px) {
          .octa-header { padding: 12px 16px !important; }
          .octa-nav-link { display: none !important; }
          .octa-footer { padding: 24px 16px !important; flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>

      <Header bg={C.dark} />

      <main style={{ padding: '40px 20px', maxWidth: '1000px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '32px' }}>

        {/* BENVENUTO */}
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 900, marginBottom: '4px' }}>
            Ciao, {profile?.username} 👋
          </h1>
          <p style={{ fontSize: '15px', color: C.textLight }}>
            {isAdmin ? 'Pannello admin OCTA' : 'Le tue esperienze di viaggio ti aspettano'}
          </p>
        </div>

        {/* METRICHE UTENTE */}
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Le tue statistiche</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            {[
              { label: 'Esperienze create', value: stats.create, color: C.trasporto },
              { label: 'Pubblicate', value: stats.pubblicate, color: C.alloggio },
              { label: 'Volte copiate', value: stats.copiate, color: C.attivita },
              { label: 'Livello', value: profile?.livello || 1, color: C.servizi },
              { label: 'Punteggio', value: profile?.punteggio || 0, color: C.dark },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ backgroundColor: C.white, borderRadius: '14px', padding: '24px', border: `1px solid ${C.border}`, borderTop: `4px solid ${color}` }}>
                <p style={{ fontSize: '12px', color: C.textLight, marginBottom: '8px', fontWeight: 600 }}>{label}</p>
                <p style={{ fontSize: '30px', fontWeight: 900, color: C.text }}>{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ULTIMA ESPERIENZA */}
        {ultimaEsperienza && (
          <div style={{ backgroundColor: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px' }}>
            <p style={{ fontSize: '13px', fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Ultima esperienza</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                {[{ k: 'trasporto', c: C.trasporto }, { k: 'alloggio', c: C.alloggio }, { k: 'attivita', c: C.attivita }, { k: 'servizi', c: C.servizi }]
                  .map(s => <div key={s.k} style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: ultimaEsperienza[s.k] ? s.c : C.border }} />)}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 700, fontSize: '16px' }}>{ultimaEsperienza.destinazione}</p>
                {ultimaEsperienza.titolo && <p style={{ fontSize: '13px', color: C.textLight }}>{ultimaEsperienza.titolo}</p>}
              </div>
              {ultimaEsperienza.prezzo_totale && (
                <p style={{ fontWeight: 700, color: C.servizi }}>€{Number(ultimaEsperienza.prezzo_totale).toLocaleString('it-IT')}</p>
              )}
              <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, backgroundColor: ultimaEsperienza.is_pubblica ? '#f0fdf4' : '#f1f5f9', color: ultimaEsperienza.is_pubblica ? '#15803d' : C.textLight }}>
                {ultimaEsperienza.is_pubblica ? '● Pubblica' : '○ Privata'}
              </span>
            </div>
          </div>
        )}

        {/* AZIONI RAPIDE */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <a href="/crea" style={{ padding: '13px 28px', borderRadius: '10px', backgroundColor: C.alloggio, color: C.white, fontSize: '15px', fontWeight: 700 }}>+ Crea esperienza</a>
          <a href="/bacheca" style={{ padding: '13px 28px', borderRadius: '10px', border: `1px solid ${C.border}`, color: C.text, fontSize: '15px', fontWeight: 600, backgroundColor: C.white }}>Esplora bacheca</a>
          <a href="/profilo" style={{ padding: '13px 28px', borderRadius: '10px', border: `1px solid ${C.border}`, color: C.text, fontSize: '15px', fontWeight: 600, backgroundColor: C.white }}>Il mio profilo</a>
        </div>

        {/* ── SEZIONE ADMIN ── */}
        {isAdmin && adminStats && (
          <>
            <div style={{ borderTop: `2px dashed ${C.border}`, paddingTop: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <span style={{ padding: '4px 12px', borderRadius: '6px', backgroundColor: C.trasporto, color: C.white, fontSize: '11px', fontWeight: 900, letterSpacing: '0.08em' }}>ADMIN</span>
                <h2 style={{ fontSize: '16px', fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Metriche piattaforma</h2>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '28px' }}>
                {[
                  { label: 'Utenti registrati', value: adminStats.totUtenti || 0, color: C.alloggio },
                  { label: 'Esperienze pubblicate', value: adminStats.totEsp || 0, color: C.attivita },
                  { label: 'Early adopters', value: adminStats.totAdopters || 0, color: C.servizi },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ backgroundColor: C.dark, borderRadius: '14px', padding: '24px', borderTop: `4px solid ${color}` }}>
                    <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', fontWeight: 600 }}>{label}</p>
                    <p style={{ fontSize: '30px', fontWeight: 900, color: C.white }}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Ultime esperienze create */}
              {adminStats.ultime.length > 0 && (
                <div style={{ backgroundColor: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                  <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}` }}>
                    <p style={{ fontSize: '13px', fontWeight: 700, color: C.text }}>Ultime esperienze pubblicate</p>
                  </div>
                  {adminStats.ultime.map((e, i) => (
                    <div key={e.id} style={{ padding: '14px 24px', borderBottom: i < adminStats.ultime.length - 1 ? `1px solid ${C.border}` : 'none', display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        {[{ k: 'trasporto', c: C.trasporto }, { k: 'alloggio', c: C.alloggio }, { k: 'attivita', c: C.attivita }, { k: 'servizi', c: C.servizi }]
                          .map(s => <div key={s.k} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: e[s.k] ? s.c : C.border }} />)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 700, fontSize: '14px' }}>{e.destinazione}</p>
                        <p style={{ fontSize: '12px', color: C.textLight }}>di {e.profiles?.username || '—'}</p>
                      </div>
                      {e.prezzo_totale && <span style={{ fontSize: '13px', fontWeight: 700, color: C.servizi, flexShrink: 0 }}>€{Number(e.prezzo_totale).toLocaleString('it-IT')}</span>}
                      {e.durata_notti && <span style={{ fontSize: '12px', color: C.textLight, flexShrink: 0 }}>🌙 {e.durata_notti}n</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* FOOTER */}
      <footer className="octa-footer" style={{ padding: '32px 40px', borderTop: `1px solid rgba(255,255,255,0.08)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: C.dark, flexWrap: 'wrap', gap: '16px' }}>
        <img src="/images/logo_bianco.png" alt="OCTA" style={{ height: '32px', objectFit: 'contain' }} />
        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>© 2025 OCTA — Online Complete Travel Agency</p>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['trasporto', 'alloggio', 'attivita', 'servizi'].map(k => (
            <span key={k} style={{ width: '12px', height: '12px', borderRadius: '50%', display: 'inline-block', backgroundColor: C[k] }} />
          ))}
        </div>
      </footer>
    </div>
  );
}
